import { useEffect, useState, useRef } from 'react'
import { Box } from '@mui/material'
import { Loader } from '@googlemaps/js-api-loader'
import { useGoogleClusterMap } from 'packages/wimap/Google'
import { MapContainer } from 'pages/PagesStyles'

import MapLoader from 'components/Loader'
import MapLegend from './MapLegend'
import useMarkers from './use-markers'

const styles = [
  {
    'featureType': 'poi',
    'stylers': [{ 'visibility': 'off' }]
  },
  {
    'featureType': 'poi.park',
    'elementType': 'geometry',
    'stylers': [{ 'visibility': 'on' }]
  },
  {
    'featureType': 'transit',
    'stylers': [{ 'visibility': 'off' }]
  }
]

const Cartography = ({
  google,
  map,
  cluster,
  sites,
  infoWindow,
  hasZoom,
  onMarkerClick,
  geolocation,
  disableMarkerPopup = false
}) => {
  const markersRef = useRef([])
  const geolocationMarkerRef = useRef(null)
  const toMarker = useMarkers({
    google,
    map,
    infoWindow,
    onMarkerClick,
    disablePopup: disableMarkerPopup
  })

  const initializeGeoloc = () => {
    const { latitude, longitude } = geolocation.coords
    map.setCenter(new google.maps.LatLng(latitude, longitude))
    map.setZoom(15)

    const position = new google.maps.LatLng(latitude, longitude)

    if (geolocationMarkerRef.current) {
      geolocationMarkerRef.current.setPosition(position)
    } else {
      geolocationMarkerRef.current = new google.maps.Marker({
        position,
        map,
        icon: {
          path: google.maps.SymbolPath.CIRCLE,
          scale: 10,
          fillColor: '#4285F4',
          fillOpacity: 1,
          strokeColor: '#FFFFFF',
          strokeWeight: 3,
        },
        zIndex: 999,
        title: 'Votre position'
      })
    }

    return () => {
      if (geolocationMarkerRef.current) {
        geolocationMarkerRef.current.setMap(null)
        geolocationMarkerRef.current = null
      }
    }
  }

  const addMarkers = () => {
    if (!cluster || !map || !google) return
    if (typeof cluster.clearMarkers !== 'function' || typeof cluster.addMarkers !== 'function') return
    if (!cluster.algorithm) return

    try {
      cluster.clearMarkers()
      markersRef.current.forEach(marker => marker?.setMap?.(null))
      markersRef.current = sites.map((site) => {
        const marker = toMarker(site)
        marker?.setMap?.(map)
        return marker
      })

      const validMarkers = markersRef.current.filter(marker => marker?.position)

      if (validMarkers.length > 0) {
        cluster.addMarkers(validMarkers)
      }
    }
    catch (e) {
      console.log(e)
    }

  }
  useEffect(() => {
    if (!cluster || !map || !google || !geolocation.active) return

    return initializeGeoloc()
  }, [cluster, map, google, geolocation.active])

  useEffect(() => {
    if (!cluster || !map || !google) return

    addMarkers()

    if (geolocation.active) return
    if (!hasZoom) {
      map.setZoom(12)
      map.setCenter(markersRef.current[0]?.position)
    } else if (markersRef.current.length === 1) {
      map.setZoom(18)
      map.setCenter(markersRef.current[0]?.position)
    } else if (markersRef.current.length) {
      const bounds = new google.maps.LatLngBounds()
      markersRef.current.forEach((marker) => {
        if (marker?.position) bounds.extend(marker.position)
      })
      map.fitBounds(bounds)
    } else {
      map.setCenter(new google.maps.LatLng(48.873259, 2.305976))
      map.setZoom(15)
    }

  }, [sites, cluster, map, google, hasZoom, geolocation.active])

  return null
}

const LoaderMap = ({
  google,
  isSupervisionView,
  hasZoom = true,
  mapOptions = {
    center: { lat: 48.873259, lng: 2.305976 },
    mapTypeId: 'roadmap',
    disableDefaultUI: true,
    styles,
  },
 algorithmOption = {
    minPoints: 5,
    radius: 60,
    maxZoom: 16,
    minZoom: 0,
    extent: 512,
    nodeSize: 64
  },
  activeEquipment,
  sites,
  onMarkerClick,
  isExpanded = false,
  onToggleExpand = () => {},
  showExpandButton = true,
  geolocation = { active: false },
  isProjectHomeView = false,
  hideLegends = false,
  disableMarkerPopup = false
}) => {
  const infoWindow = useRef(null)

  const handleGeolocationChange = ({ latitude, longitude }) => {
    //setGeolocationCoords({ latitude, longitude })
    geolocation.onChange({ latitude, longitude })
    maps.map.setCenter({ lat: latitude, lng: longitude })
  }

  const handleGeolocationReset = () => {
    //setGeolocationCoords({ latitude, longitude })
    const { latitude, longitude } = geolocation.onReset()
    maps.map.setCenter({ lat: latitude, lng: longitude })
  }
  // TODO: Replace type when defined by @center/wimap
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const maps = useGoogleClusterMap({
    google,
    mapOptions,
    algorithmOption,
    activeEquipment,
    infoWindow,
    hasZoom,
    isExpanded,
    onToggleExpand,
    showExpandButton,
    isProjectHomeView,
    hideLegends,
    onGeolocationReset: geolocation.active ? handleGeolocationReset : null,
    onGeolocationChange: geolocation.active ? handleGeolocationChange : null
  });

  useEffect(() => {
    if (!maps.map || !geolocation.active) return

    const handleDragEnd = () => {
      const center = maps.map.getCenter()
      if (center) {
        geolocation.onChange({
          latitude: center.lat(),
          longitude: center.lng()
        })
      }
    }

    const listener = maps.map.addListener('dragend', handleDragEnd)

    return () => {
      google.maps.event.removeListener(listener)
    }
  }, [maps.map, google, geolocation.active])

  return (
    <MapContainer sx={{ position: 'relative' }}>
      <Box 
        id='mapViewGlobal' 
        sx={{
          flexGrow: 1,
          borderRadius: isSupervisionView ? '0 24px 24px 0' : (isExpanded ? 0 : '24px'),
          height: '100%',
          width: '100%',
          position: 'relative',
          transition: 'border-radius 0.3s ease-in-out'
        }} 
      />
      <Box 
        id='infoWindowGlobal' 
        ref={infoWindow}
        sx={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          pointerEvents: 'none',
          '& > *': {
            pointerEvents: 'auto'
          }
        }}
      />
      {maps.ready
        ? <Cartography
            infoWindow={infoWindow}
            google={google}
            map={maps.map}
            cluster={maps.cluster}
            sites={sites}
            activeEquipment={activeEquipment}
            hasZoom={hasZoom}
            onMarkerClick={onMarkerClick}
            geolocation={geolocation}
            disableMarkerPopup={disableMarkerPopup}
        />
        : <MapLoader />
      }
      {!hideLegends && (
        <Box position="absolute" display="flex" bottom="30px" left="30px" sx={{ zIndex: 10 }}>
          <MapLegend />
        </Box>
      )}
    </MapContainer>
  )
};

const LoaderGoogle = ({ 
  hasZoom = true, 
  isExpanded = false,
  onToggleExpand = () => {},
  showExpandButton = true,
  isProjectHomeView = false,
  hideLegends = false,
  geolocation = {  active: false },
  ...props 
}) => {
  const [google, setGoogle] = useState(null)
  
  useEffect(() => {
    const apiKey = process.env.REACT_APP_GOOGLE_MAP_KEY
    if (google === null && apiKey) {
      ;(async () => {
        const loader = new Loader({
          apiKey,
          libraries: ['geometry'],
          version: 'weekly'
        })
        loader.load().then(google => setGoogle(google))
      })()
    }
  }, [google])

  if (!google) {
    return <MapLoader />
  }

  return (
    <LoaderMap 
      google={google} 
      {...props} 
      hasZoom={hasZoom}
      isExpanded={isExpanded}
      onToggleExpand={onToggleExpand}
      showExpandButton={showExpandButton}
      isProjectHomeView={isProjectHomeView}
      hideLegends={hideLegends}
      geolocation={geolocation}
    />
  )
}

export default LoaderGoogle
